// AppContext.js — providers globales del frontend de News.
//
// Contextos que expone:
//   NewspaperProvider   → config del periódico, categorías, última edición.
//   AuthProvider        → autenticación via Ecolab (JWT + refresh token).
//   ReadingListProvider → artículos guardados en localStorage (lector).
//   AppProviders        → wrapper que anida los 3 (usado en index.js).

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { categoryService, configService, editionService, authService, resolvePeriodicoId, geographyService } from '../services/api';
import { locationService } from '../services/locationService';

// ═══════════════════════════════════════════════════════════════════════
//  Configuración
// ═══════════════════════════════════════════════════════════════════════
const ECOLAB_API_URL =
  process.env.REACT_APP_ECOLAB_API_URL ||
  'https://ecolab-backend-723916953839.us-central1.run.app';

// Claves de storage. Mantenemos 'newspaperToken' por compatibilidad con
// el frontend histórico. Los otros son nuevos para el flujo Ecolab.
const TOKEN_KEY   = 'newspaperToken';
const REFRESH_KEY = 'newspaperRefreshToken';
const USER_KEY    = 'newspaperUser';

// ═══════════════════════════════════════════════════════════════════════
//  NewspaperContext — datos del periódico (config + categorías)
// ═══════════════════════════════════════════════════════════════════════
const NewspaperContext = createContext(null);

export function NewspaperProvider({ children }) {
  const [config, setConfig]                 = useState(null);
  const [categories, setCategories]         = useState([]);
  const [latestEdition, setLatestEdition]   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  // Región activa del lector: null hasta que se resuelve. Cuando cambia,
  // se vuelve a pedir la última edición para reflejar la regional.
  const [regionId, setRegionId]             = useState(null);
  const [regionInfo, setRegionInfo]         = useState({ departmentId: null });
  // Catálogo de deptos para el selector manual (lazy: se carga cuando
  // el header lo pida).
  const [departamentos, setDepartamentos]   = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await resolvePeriodicoId();
        // Resolver región del lector en paralelo con la config: la
        // primera visita puede tardar unos segundos por el prompt de
        // geolocation; el resto de datos no espera por eso.
        const regionPromise = locationService.getRegion();
        const [configData, categoriesData] = await Promise.all([
          configService.get(),
          categoryService.getAll(),
        ]);
        setConfig(configData);
        setCategories(categoriesData);

        const resolvedRegion = await regionPromise;
        // getRegion ahora devuelve { municipioId, departmentId, name, ... } | null
        const deptId = resolvedRegion?.departmentId ?? null;
        setRegionId(deptId);
        setRegionInfo(locationService.getRegionInfo());
        // Solo ahora pedimos la última edición, con la región resuelta
        // (así el backend elige regional vs. global correctamente).
        const latest = await editionService.getLatest({ departmentId: deptId });
        setLatestEdition(latest);
      } catch (err) {
        setError(err.message);
        console.error('Error loading initial data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshData = useCallback(async () => {
    try {
      const [categoriesData, latestEditionData] = await Promise.all([
        categoryService.getAll(),
        editionService.getLatest({ departmentId: regionId }),
      ]);
      setCategories(categoriesData);
      setLatestEdition(latestEditionData);
    } catch (err) {
      console.error('Error refreshing data:', err);
    }
  }, [regionId]);

  // Selector manual: el usuario eligió una región. Persistimos, refrescamos
  // la última edición para reflejarla, y actualizamos el label.
  const setManualRegion = useCallback(async ({ departmentId, name }) => {
    if (departmentId == null) {
      locationService.clearManualRegion();
    } else {
      locationService.setManualRegion({ departmentId, name });
    }
    setRegionId(departmentId);
    setRegionInfo(locationService.getRegionInfo());
    try {
      const latest = await editionService.getLatest({ departmentId });
      setLatestEdition(latest);
    } catch (err) {
      console.error('Error refreshing latest edition:', err);
    }
  }, []);

  // Catálogo de deptos para el dropdown (lazy).
  const loadDepartamentos = useCallback(async () => {
    if (departamentos.length > 0) return departamentos;
    try {
      const list = await geographyService.getAllDepartments();
      setDepartamentos(list);
      return list;
    } catch (err) {
      console.error('Error loading departamentos:', err);
      return [];
    }
  }, [departamentos]);

  return (
    <NewspaperContext.Provider
      value={{
        config, categories, latestEdition, loading, error, refreshData,
        // Región activa (para pintar el selector y para que otras vistas
        // puedan filtrar por depto en el futuro).
        regionId, regionInfo, departamentos,
        setManualRegion, loadDepartamentos,
      }}
    >
      {children}
    </NewspaperContext.Provider>
  );
}

export function useNewspaper() {
  const context = useContext(NewspaperContext);
  if (!context) throw new Error('useNewspaper must be used within a NewspaperProvider');
  return context;
}

// ═══════════════════════════════════════════════════════════════════════
//  AuthContext — autenticación Ecolab
// ═══════════════════════════════════════════════════════════════════════
//
// Flujo:
//   1. login(email, password) → POST {ECOLAB_API_URL}/api/ecolab/auth/login
//   2. Ecolab responde { accessToken, refreshToken, expiresIn, usuario }
//   3. Guardamos accessToken en localStorage → api.js lo adjunta a las
//      llamadas al backend .NET como Authorization: Bearer.
//   4. El backend .NET valida la firma con la Token:Key compartida y
//      aprovisiona la fila en news_main.usuarios_cache automáticamente
//      (EnsureNewsUserFilter).
//   5. refresh(): cuando el accessToken expira, POST /api/ecolab/auth/refresh
//      con el refreshToken para obtener uno nuevo.
//   6. Sync cross-tab por evento 'storage': si otra pestaña hace logout,
//      esta también.
//
// Nota: patrón portado del frontend del Marketplace B2B (redeprog/b2b_frontend,
// context/AuthContext.js). Se simplifica para News: no hay multi-tenant
// activo en runtime (el periódico se define por REACT_APP_PERIODICO_ID),
// así que se elimina toda la lógica de X-Cooperativa-Id / selector.

const AuthContext = createContext(null);

// Decodifica el payload de un JWT sin verificar firma (sólo lectura).
// Ojo: JWT payloads con acentos (López, González, Ñoño) llegan
// codificados en UTF-8 dentro del base64. atob() devuelve una "binary
// string" (cada char = 1 byte), que si se pasa directo a JSON.parse
// interpreta esos bytes como Latin-1 y produce "LÃ³pez" en lugar de
// "López". El fix es convertir la binary string a bytes reales y
// decodificar como UTF-8 con TextDecoder.
const decodeJwt = (jwt) => {
  try {
    const [, payload] = jwt.split('.');
    // atob no maneja base64url, hay que traducir a base64 estándar.
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const binary = atob(b64 + pad);
    // Reconstruir bytes → decodificar UTF-8 correcto.
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const jsonUtf8 = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonUtf8);
  } catch {
    return null;
  }
};

// Toma el JWT de Ecolab (payload) y arma el objeto user que consume
// el resto de la app. Ecolab manda claims cortos: nameid (id), email,
// name, family_name, role (string o array).
const buildUserFromJwt = (jwt, extra = {}) => {
  const p = decodeJwt(jwt) || {};
  const roles = Array.isArray(p.role) ? p.role : (p.role ? [p.role] : []);
  return {
    id:         p.nameid || p.sub || extra.id || null,
    email:      p.email || extra.email || null,
    name:       [p.name, p.family_name].filter(Boolean).join(' ')
                || extra.name
                || p.email
                || null,
    roles,
    // 'role' plano para plantillas legacy que esperan un string.
    role:       roles[0] || 'staff',
    perfilId:   p.perfilId ?? extra.perfilId,
    exp:        p.exp,  // segundos epoch; sirve para prever vencimiento
  };
};

export function AuthProvider({ children }) {
  const [user,  setUser]  = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Roles INTERNOS de News del usuario logueado (Editor, Redactor…).
  // null = aún no consultados. Array = respuesta del backend.
  // Array vacío = usuario autorizado a la app pero sin rol asignado.
  const [newsRoles, setNewsRoles] = useState(null);

  // ── Hidratar desde localStorage al montar ──────────────────────────
  useEffect(() => {
    try {
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser  = localStorage.getItem(USER_KEY);
      if (savedToken) {
        setToken(savedToken);
        if (savedUser) {
          try { setUser(JSON.parse(savedUser)); }
          catch { setUser(buildUserFromJwt(savedToken)); }
        } else {
          setUser(buildUserFromJwt(savedToken));
        }
      }
    } catch (e) {
      // localStorage no disponible (Safari en incógnito, etc.)
      console.warn('[AuthContext] localStorage no disponible:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Sync cross-tab: logout global via evento 'storage' ─────────────
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY && e.newValue === null) {
        setUser(null);
        setToken(null);
        setNewsRoles(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Cargar roles INTERNOS de News tras autenticar ─────────────────
  // El 'aud' del JWT confirma que el usuario tiene acceso a la app
  // News (nivel 1). Los roles internos determinan qué puede HACER
  // dentro (nivel 2). Se cargan una sola vez por sesión activa.
  useEffect(() => {
    if (!token) {
      setNewsRoles(null);
      return;
    }
    let cancelled = false;
    import('../services/api').then(({ authService }) => {
      authService.getNewsRoles()
        .then(roles => { if (!cancelled) setNewsRoles(roles); })
        .catch(() => { if (!cancelled) setNewsRoles([]); });
    });
    return () => { cancelled = true; };
  }, [token]);

  // ── login(email, password, turnstileToken) → Ecolab ─────────────────
  const login = useCallback(async (email, password, turnstileToken = '') => {
    try {
      const resp = await fetch(`${ECOLAB_API_URL}/api/ecolab/auth/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ email, password, turnstileToken }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        // Distinguir error de Turnstile de error de credenciales
        if (err.turnstileError) {
          return { success: false, error: err.mensaje || 'Verificación de seguridad fallida. Recarga la página.' };
        }
        return { success: false, error: err.message || err.error || err.mensaje || `Error ${resp.status}` };
      }

      const data = await resp.json();
      // Ecolab puede devolver el token con varios nombres. Cubrimos todos.
      const accessToken =
        data.accessToken || data.access_token || data.token || data.jwt;
      const refreshToken =
        data.refreshToken || data.refresh_token || null;

      if (!accessToken) {
        console.error('[AuthContext] Ecolab respondió 200 pero sin JWT. Body keys:', Object.keys(data));
        return { success: false, error: 'El servidor no devolvió un token válido.' };
      }

      // El body puede traer datos del usuario en 'usuario' (español) o
      // 'user' (inglés). Si no, los sacamos del payload del JWT.
      const userData = data.usuario || data.user || {};
      const sessionUser = buildUserFromJwt(accessToken, {
        id:    userData.id || userData.aspNetUserId,
        email: userData.email,
        name:  userData.name || [userData.nombre, userData.apellido].filter(Boolean).join(' '),
        perfilId: userData.perfilId,
      });

      setToken(accessToken);
      setUser(sessionUser);
      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(sessionUser));
      if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);

      return { success: true, user: sessionUser };
    } catch (err) {
      console.error('[AuthContext] login error:', err);
      return { success: false, error: 'No se pudo contactar al servidor.' };
    }
  }, []);

  // ── logout ─────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  // ── refresh session con refreshToken ───────────────────────────────
  const refreshSession = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) { logout(); return false; }
    try {
      const resp = await fetch(`${ECOLAB_API_URL}/api/ecolab/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!resp.ok) { logout(); return false; }
      const data = await resp.json();
      const newToken     = data.accessToken || data.access_token || data.token;
      const newRefresh   = data.refreshToken || data.refresh_token;
      if (!newToken) { logout(); return false; }
      setToken(newToken);
      setUser(buildUserFromJwt(newToken));
      localStorage.setItem(TOKEN_KEY, newToken);
      if (newRefresh) localStorage.setItem(REFRESH_KEY, newRefresh);
      return true;
    } catch (e) {
      logout();
      return false;
    }
  }, [logout]);

  // ── refrescar perfil desde el backend .NET ─────────────────────────
  // Enriquece `user` con datos que sólo viven en news_main.usuarios_cache
  // (avatar guardado localmente, teléfono, etc.). No bloquea el login.
  const refreshProfile = useCallback(async () => {
    if (!token) return null;
    try {
      const perfil = await authService.getProfile();
      setUser((prev) => {
        const enriched = { ...(prev || {}), ...perfil };
        try { localStorage.setItem(USER_KEY, JSON.stringify(enriched)); } catch {}
        return enriched;
      });
      return perfil;
    } catch (e) {
      console.warn('[AuthContext] refreshProfile falló:', e.message);
      return null;
    }
  }, [token]);

  const isAuthenticated = !!user && !!token;

  const hasRole = useCallback(
    (roleName) => !!user && (user.role === roleName || user.roles?.includes(roleName)),
    [user]);

  // ── Helpers para roles INTERNOS de News (Editor/Redactor/...) ────
  // Diferentes de hasRole() que lee roles del JWT de Ecolab.
  //
  // Regla especial: EditorNacional CUENTA como Editor a efectos de
  // UI (ve todos los tabs, botones, etc.). El scope regional se
  // aplica aparte en el backend (los EditorNacional ignoran la
  // tabla usuarios_editores_regiones). Con esto evitamos escribir
  // `hasNewsRole('Editor') || hasNewsRole('EditorNacional')` en
  // decenas de sitios del frontend.
  const hasNewsRole = useCallback(
    (roleName) => {
      if (!Array.isArray(newsRoles)) return false;
      if (newsRoles.includes(roleName)) return true;
      // Escalera implícita: EditorNacional > Editor
      if (roleName === 'Editor' && newsRoles.includes('EditorNacional')) return true;
      return false;
    },
    [newsRoles]);

  const canAccessAdmin = Array.isArray(newsRoles) && newsRoles.length > 0;

  return (
    <AuthContext.Provider
      value={{
        user, token, loading, isAuthenticated,
        login, logout, refreshSession, refreshProfile,
        hasRole,
        // Roles internos de News (segunda capa de autorización)
        newsRoles, hasNewsRole, canAccessAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

// ═══════════════════════════════════════════════════════════════════════
//  ReadingListContext — artículos guardados por el lector
// ═══════════════════════════════════════════════════════════════════════
const ReadingListContext = createContext(null);

export function ReadingListProvider({ children }) {
  const [savedArticles, setSavedArticles] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('savedArticles');
    if (saved) {
      try { setSavedArticles(JSON.parse(saved)); }
      catch { localStorage.removeItem('savedArticles'); }
    }
  }, []);

  const saveArticle = useCallback((article) => {
    setSavedArticles(prev => {
      if (prev.some(a => a.id === article.id)) return prev;
      const updated = [...prev, article];
      localStorage.setItem('savedArticles', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const removeArticle = useCallback((articleId) => {
    setSavedArticles(prev => {
      const updated = prev.filter(a => a.id !== articleId);
      localStorage.setItem('savedArticles', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isArticleSaved = useCallback(
    (articleId) => savedArticles.some(a => a.id === articleId),
    [savedArticles]);

  return (
    <ReadingListContext.Provider
      value={{
        savedArticles,
        addToReadingList:      saveArticle,
        removeFromReadingList: removeArticle,
        isInReadingList:       isArticleSaved,
      }}
    >
      {children}
    </ReadingListContext.Provider>
  );
}

export function useReadingList() {
  const context = useContext(ReadingListContext);
  if (!context) throw new Error('useReadingList must be used within a ReadingListProvider');
  return context;
}

// ═══════════════════════════════════════════════════════════════════════
//  Wrapper combinado
// ═══════════════════════════════════════════════════════════════════════
export function AppProviders({ children }) {
  return (
    <AuthProvider>
      <NewspaperProvider>
        <ReadingListProvider>
          {children}
        </ReadingListProvider>
      </NewspaperProvider>
    </AuthProvider>
  );
}
